import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Download, ExternalLink,
  Loader2, X, XCircle, ZoomIn, ZoomOut,
} from 'lucide-react';
import { adminApi } from '../../services/api.jsx';
import { TuileFichier, typeDeFichier } from './typeDeFichier';
import { BarreProgression } from '../../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LIRE UN DOCUMENT SANS QUITTER LA PLATEFORME

   Le fichier est récupéré avec le jeton (GET documents/<id>/fichier/), puis
   affiché depuis une URL locale : `/media/` n'est pas lisible d'un autre
   domaine, et Django interdit qu'on l'encadre. Le type vient de
   l'extension, jamais du fichier.

   À droite, la fiche du document et, s'il attend une décision, l'examen :
   on lit et on tranche au même endroit. ← et → passent d'un document à
   l'autre, Échap ferme.
   ═══════════════════════════════════════════════════════════════════════ */

const STATUTS = {
  soumis: { libelle: 'À examiner', classe: 'etiq-attente' },
  valide: { libelle: 'Validé', classe: 'etiq-ok' },
  rejete: { libelle: 'Rejeté', classe: 'etiq-refus' },
};

const TEXTE_MAX = 200_000;

function dateLongue(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Contenu({ doc, onUrl }) {
  const type = typeDeFichier(doc);
  const [etat, setEtat] = useState({ chargement: true });
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    let url = null;
    let annule = false;
    setEtat({ chargement: true });
    setZoom(false);
    adminApi.fichierDocument(doc.id)
      .then(async (brut) => {
        if (annule) return;
        if (type.apercu === 'texte') {
          const texte = await brut.text();
          if (!annule) setEtat({ texte: texte.length > TEXTE_MAX ? `${texte.slice(0, TEXTE_MAX)}\n…` : texte });
        }
        // Le type est reposé ici : celui de la réponse n'est pas cru.
        url = URL.createObjectURL(new Blob([brut], { type: type.mime || 'application/octet-stream' }));
        onUrl(url);
        if (!annule && type.apercu !== 'texte') setEtat({ url });
      })
      .catch((e) => { if (!annule) setEtat({ erreur: e?.message || 'Le fichier n’a pas pu être chargé.' }); });
    return () => {
      annule = true;
      onUrl(null);
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.id, type.apercu, type.mime, onUrl]);

  if (etat.chargement) {
    return (
      <div className="h-full grid place-items-center p-6">
        <div className="w-full max-w-xs"><BarreProgression libelle="Préparation de l’aperçu…" /></div>
      </div>
    );
  }
  if (etat.erreur) {
    return (
      <div className="h-full grid place-items-center p-6">
        <div className="encart encart-alerte max-w-md"><AlertTriangle className="w-4 h-4" /><p>{etat.erreur}</p></div>
      </div>
    );
  }
  if (type.apercu === 'pdf') {
    return <iframe title={doc.nom_fichier} src={etat.url} className="w-full h-full border-0 bg-white" />;
  }
  if (type.apercu === 'image') {
    return (
      <div className="h-full overflow-auto grid place-items-center p-4 relative">
        <img src={etat.url} alt={doc.nom_fichier}
             onClick={() => setZoom((z) => !z)}
             className={`rounded-pastille shadow-carte bg-white ${zoom ? 'max-w-none cursor-zoom-out' : 'max-w-full max-h-full object-contain cursor-zoom-in'}`} />
        <button type="button" onClick={() => setZoom((z) => !z)}
                className="act act-icone act-contour act-sm absolute bottom-3 right-3"
                aria-label={zoom ? 'Ajuster à l’écran' : 'Taille réelle'} title={zoom ? 'Ajuster à l’écran' : 'Taille réelle'}>
          {zoom ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
        </button>
      </div>
    );
  }
  if (type.apercu === 'texte') {
    return (
      <pre className="h-full overflow-auto m-0 p-5 text-[12.5px] leading-relaxed whitespace-pre-wrap break-words bg-[var(--bg-card)] text-[var(--text-main)] font-mono">
        {etat.texte}
      </pre>
    );
  }
  return (
    <div className="h-full grid place-items-center p-6">
      <div className="text-center max-w-sm">
        <div className="flex justify-center mb-4"><TuileFichier doc={doc} taille="lg" /></div>
        <p className="font-semibold text-[var(--text-bold)]">Pas d’aperçu pour un fichier {type.libelle}</p>
        <p className="text-[13px] text-[var(--text-muted)] mt-1 mb-4">
          Le navigateur ne sait pas l’afficher. Téléchargez-le pour l’ouvrir avec le logiciel adapté.
        </p>
        <a href={etat.url} download={doc.nom_fichier} className="act">
          <Download className="w-4 h-4" /> Télécharger
        </a>
      </div>
    </div>
  );
}

function Examen({ doc, onDecider }) {
  const [commentaire, setCommentaire] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => { setCommentaire(''); setErreur(''); }, [doc.id]);

  const decider = async (statut) => {
    if (statut === 'rejete' && !commentaire.trim()) {
      setErreur('Dites à l’organisation ce qui ne va pas : elle devra refaire la pièce.');
      return;
    }
    setEnCours(statut);
    setErreur('');
    try {
      await onDecider(doc, statut, commentaire.trim());
    } catch (e) {
      setErreur(e?.message || 'La décision n’a pas été enregistrée.');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="border-t border-[var(--border)] pt-4 mt-4">
      <p className="surtitre text-[var(--text-muted)] mb-2">Votre décision</p>
      <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3}
                className="champ resize-none text-[13px]"
                placeholder="Commentaire pour l’organisation (obligatoire en cas de rejet)" />
      {erreur && <p className="text-[12px] text-[var(--alerte)] mt-2">{erreur}</p>}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button type="button" onClick={() => decider('valide')} disabled={!!enCours} className="act act-sm">
          {enCours === 'valide' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Valider
        </button>
        <button type="button" onClick={() => decider('rejete')} disabled={!!enCours} className="act act-sm act-danger">
          {enCours === 'rejete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Rejeter
        </button>
      </div>
    </div>
  );
}

export default function ApercuDocument({ documents, docId, onNaviguer, onFermer, onDecider, peutExaminer }) {
  const index = documents.findIndex((d) => d.id === docId);
  const doc = documents[index];
  const [url, setUrl] = useState(null);
  const type = useMemo(() => (doc ? typeDeFichier(doc) : null), [doc]);
  const surUrl = useCallback((u) => setUrl(u), []);

  const precedent = index > 0 ? documents[index - 1] : null;
  const suivant = index >= 0 && index < documents.length - 1 ? documents[index + 1] : null;

  useEffect(() => {
    const touche = (e) => {
      if (e.target.closest?.('textarea, input, select')) return;
      if (e.key === 'Escape') onFermer();
      if (e.key === 'ArrowLeft' && precedent) onNaviguer(precedent.id);
      if (e.key === 'ArrowRight' && suivant) onNaviguer(suivant.id);
    };
    document.addEventListener('keydown', touche);
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', touche);
      document.body.style.overflow = debordement;
    };
  }, [precedent, suivant, onFermer, onNaviguer]);

  if (!doc) return null;
  const st = STATUTS[doc.statut] || STATUTS.soumis;

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-[2px] p-0 sm:p-4 flex items-stretch justify-center"
         role="dialog" aria-modal="true" aria-label={`Aperçu : ${doc.nom_fichier}`}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="w-full max-w-7xl bg-[var(--bg-card)] sm:rounded-carte shadow-2xl flex flex-col overflow-hidden">
        {/* En-tête */}
        <header className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-[var(--border)]">
          <TuileFichier doc={doc} />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[var(--text-bold)] truncate">{doc.nom_fichier}</p>
            <p className="text-[12px] text-[var(--text-muted)]">
              {type.libelle}{documents.length > 1 ? ` · ${index + 1} sur ${documents.length}` : ''}
            </p>
          </div>
          <span className={`etiquette ${st.classe} hidden sm:inline-flex`}>{st.libelle}</span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => precedent && onNaviguer(precedent.id)} disabled={!precedent}
                    className="act act-icone act-tertiaire act-sm" aria-label="Document précédent" title="Document précédent (←)">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => suivant && onNaviguer(suivant.id)} disabled={!suivant}
                    className="act act-icone act-tertiaire act-sm" aria-label="Document suivant" title="Document suivant (→)">
              <ChevronRight className="w-4 h-4" />
            </button>
            {url && (
              <>
                <a href={url} target="_blank" rel="noreferrer" className="act act-icone act-tertiaire act-sm hidden sm:inline-flex"
                   aria-label="Ouvrir dans un nouvel onglet" title="Ouvrir dans un nouvel onglet">
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a href={url} download={doc.nom_fichier} className="act act-icone act-tertiaire act-sm"
                   aria-label="Télécharger" title="Télécharger">
                  <Download className="w-4 h-4" />
                </a>
              </>
            )}
            <button type="button" onClick={onFermer} className="act act-icone act-contour act-sm ml-1"
                    aria-label="Fermer" title="Fermer (Échap)">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Corps : le document, et sa fiche */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="flex-1 min-h-[55vh] lg:min-h-0 bg-[var(--bg-main)]">
            <Contenu key={doc.id} doc={doc} onUrl={surUrl} />
          </div>
          <aside className="lg:w-[320px] shrink-0 border-t lg:border-t-0 lg:border-l border-[var(--border)] p-5 overflow-y-auto">
            <span className={`etiquette ${st.classe} sm:hidden mb-3`}>{st.libelle}</span>
            <dl className="space-y-3 text-[13px]">
              {doc.demande_libelle && (
                <div><dt className="surtitre text-[var(--text-muted)]">Répond à la demande</dt><dd className="font-semibold text-[var(--text-bold)]">{doc.demande_libelle}</dd></div>
              )}
              <div><dt className="surtitre text-[var(--text-muted)]">Catégorie</dt><dd className="capitalize text-[var(--text-bold)]">{doc.categorie || '—'}</dd></div>
              <div><dt className="surtitre text-[var(--text-muted)]">Déposé le</dt><dd className="text-[var(--text-bold)]">{dateLongue(doc.upload_le)}</dd></div>
              {doc.depose_par_equipe && (
                <div><dt className="surtitre text-[var(--text-muted)]">Déposé par</dt><dd className="text-[var(--text-bold)]">{doc.depose_par_display || 'l’équipe'}, pour l’organisation</dd></div>
              )}
              {doc.projet_nature && (
                <div><dt className="surtitre text-[var(--text-muted)]">Dossier</dt><dd className="text-[var(--text-bold)]">{doc.projet_nature}</dd></div>
              )}
              {doc.statut !== 'soumis' && (doc.valide_par_display || doc.valide_le) && (
                <div>
                  <dt className="surtitre text-[var(--text-muted)]">{doc.statut === 'valide' ? 'Validé par' : 'Rejeté par'}</dt>
                  <dd className="text-[var(--text-bold)]">
                    {[doc.valide_par_display, doc.valide_le && `le ${new Date(doc.valide_le).toLocaleDateString('fr-FR')}`].filter(Boolean).join(', ')}
                  </dd>
                </div>
              )}
              {doc.commentaire_coach && (
                <div><dt className="surtitre text-[var(--text-muted)]">Commentaire</dt><dd className="text-[var(--text-main)] whitespace-pre-line">« {doc.commentaire_coach} »</dd></div>
              )}
            </dl>
            {peutExaminer && doc.statut === 'soumis' && <Examen doc={doc} onDecider={onDecider} />}
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  );
}
